import React from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import styled from 'styled-components'

import Tooltip from 'components/Tooltip'
import Spinner from 'components/Spinner'

import { compareVersions } from 'helpers/versionCompare'
import { color } from 'styles/colors'
import {
  isModelFirmwareStatusReportable,
  doesFirmwareModelMatchComponent
} from 'pages/Device/SystemComponentsPanel/helpers/firmwareStatus'

type DefaultFirmwareLoad = {
  model: string
  versionNumber: string
}

type GetFirmwareStatusParams = {
  modelNumber: string
  currentVersion: string | null
  defaultFirmwareLoads: DefaultFirmwareLoad[] | null
  areDefaultFirmwareLoadsLoaded: boolean
  component?: 'status' | 'settings' | 'display-component'
}

const RenderFirmwareStatus = ({
  modelNumber,
  currentVersion,
  defaultFirmwareLoads,
  areDefaultFirmwareLoadsLoaded,
  component = 'display-component'
}: GetFirmwareStatusParams) => {
  const renderStatusIndicator = () => {
    if (!areDefaultFirmwareLoadsLoaded) {
      return (
        <S.Spinner
          variant="cell"
          aria-label="firmware versions loading"
          component={component}
        />
      )
    }

    if (!defaultFirmwareLoads || defaultFirmwareLoads.length === 0) {
      return null
    }

    if (!isModelFirmwareStatusReportable(modelNumber)) {
      const directMatch = defaultFirmwareLoads.find(
        (firmware) => firmware.model === modelNumber
      )

      if (!directMatch) {
        return null
      }

      const comparisonResult = compareVersions(
        currentVersion,
        directMatch.versionNumber
      )

      if (comparisonResult >= 0) {
        return (
          <Tooltip title="Firmware version is up-to-date.">
            <FirmwareStatus component={component}>
              <CheckCircleIcon />
            </FirmwareStatus>
          </Tooltip>
        )
      }

      if (component === 'display-component') {
        return (
          <Tooltip
            title={`A newer firmware version, ${directMatch.versionNumber}, is available.`}
          >
            <FirmwareStatusText>Update Available</FirmwareStatusText>
          </Tooltip>
        )
      }

      return null
    }

    const defaultFirmwareLoad = defaultFirmwareLoads.find((firmware) =>
      doesFirmwareModelMatchComponent(firmware.model, modelNumber)
    )

    if (!defaultFirmwareLoad) {
      return null
    }

    const comparisonResult = compareVersions(currentVersion, defaultFirmwareLoad.versionNumber)

    if (comparisonResult >= 0) {
      return (
        <Tooltip title="Firmware version is up-to-date.">
          <FirmwareStatus component={component}>
            <CheckCircleIcon />
          </FirmwareStatus>
        </Tooltip>
      )
    }

    if (component === 'display-component') {
      return (
        <Tooltip
          title={`A newer firmware version, ${defaultFirmwareLoad.versionNumber}, is available.`}
        >
          <FirmwareStatusText>Update Available</FirmwareStatusText>
        </Tooltip>
      )
    }

    return null
  }

  return (
    <FirmwareContainer>
      <FirmwareVersion component={component}>{currentVersion || 'N/A'}</FirmwareVersion>
      {currentVersion && renderStatusIndicator()}
    </FirmwareContainer>
  )
}

export default RenderFirmwareStatus

const S = {
  Spinner: styled(Spinner)`
    display: inline-block;
    margin-left: 5px;
    width: 12px;
    height: 12px;
    vertical-align: middle;
    margin-top: 0;
  `
}

const FirmwareContainer = styled.div`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
`

const FirmwareVersion = styled.span<{
  component?: 'status' | 'settings' | 'display-component'
}>`
  display: inline-flex;
  align-items: center;
`

const FirmwareStatus = styled.div<{
  component?: 'status' | 'settings' | 'display-component'
}>`
  display: inline-flex;
  align-items: center;
  color: ${color('successIcon')};
  font-size: 12px;
  padding-left: 5px;
  cursor: pointer;

  svg {
    font-size: ${(props) => props.component === 'settings' ? '14px' : '15px'} !important;
    margin-top: 0;
  }
`

const FirmwareStatusText = styled.span`
  padding-left: 5px;
  text-transform: capitalize;
  color: ${color('ancillaryText')};
  cursor: pointer;
  font-weight: normal !important;
`
